import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { createOrderFormSchema, type OrderFormValues } from "../../types/order";
import "./offer-form.css";
import { useI18n } from "../../i18n/I18n";

interface OfferFormProps {
  offerDoneActive: boolean;
  setOfferDoneActive: React.Dispatch<React.SetStateAction<boolean>>;
  title?: string;
  submitLabel?: string;
  onSuccess?: () => void;
}

function OfferForm({ offerDoneActive, setOfferDoneActive, title, submitLabel, onSuccess }: OfferFormProps) {
  const { language, t } = useI18n();
  const resolvedTitle = title ?? t("checkout");
  const resolvedSubmitLabel = submitLabel ?? t("sendRequest");
  const [sent, setSent] = useState(false);
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<OrderFormValues>({
    resolver: zodResolver(createOrderFormSchema(language)),
    mode: "onTouched",
    defaultValues: { name: "", phone: "+380", email: "" },
  });

  useEffect(() => {
    if (!offerDoneActive) {
      setSent(false);
      reset();
    }
  }, [offerDoneActive, reset]);

  const submit = handleSubmit(async () => {
    reset();
    onSuccess?.();
    setSent(true);
  });
  if (!offerDoneActive) return null;

  return (
    <div className="offer-form active" onMouseDown={() => setOfferDoneActive(false)} role="dialog" aria-modal="true" aria-labelledby="order-form-title">
      <div className="offer-form__container" onMouseDown={(event) => event.stopPropagation()}>
        <button className="offer-form__close" onClick={() => setOfferDoneActive(false)} aria-label={t("close")}>×</button>
        {sent ? (
          <div className="offer-form__success"><span>✓</span><h2>{t("requestAccepted")}</h2><p>{t("contactSoon")}</p><button className="button" onClick={() => setOfferDoneActive(false)}>{t("done")}</button></div>
        ) : (
          <>
            <p className="eyebrow">{t("leaveContacts")}</p>
            <h2 id="order-form-title">{resolvedTitle}</h2>
            <p className="offer-form__lead">{t("formLead")}</p>
            <form className="offer-form__form" onSubmit={submit} noValidate>
              <label>{t("yourName")}
                <input {...register("name")} autoFocus autoComplete="name" placeholder={t("namePlaceholder")} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? "name-error" : undefined} />
                {errors.name && <span id="name-error" className="offer-form__error">{errors.name.message}</span>}
              </label>
              <label>{t("phone")}
                <input {...register("phone")} type="tel" inputMode="tel" autoComplete="tel" placeholder="+380 __ ___ __ __" aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? "phone-error" : undefined} />
                {errors.phone && <span id="phone-error" className="offer-form__error">{errors.phone.message}</span>}
              </label>
              <label>Email <small>{t("optional")}</small>
                <input {...register("email")} type="email" autoComplete="email" placeholder="name@example.com" aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? "email-error" : undefined} />
                {errors.email && <span id="email-error" className="offer-form__error">{errors.email.message}</span>}
              </label>
              <button className="button" type="submit" disabled={isSubmitting}>{isSubmitting ? t("sending") : resolvedSubmitLabel}</button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default OfferForm;
